import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';

interface ConfirmDeclineActionsProps {
  bookingId: string;
  onSuccess?: () => void;
}

export const ConfirmDeclineActions: React.FC<ConfirmDeclineActionsProps> = ({
  bookingId,
  onSuccess,
}) => {
  const { t } = useTranslation('dashboard');
  const { toast } = useToast();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [declineReason, setDeclineReason] = useState('');

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      const { error } = await supabase.functions.invoke('confirm-booking', {
        body: { bookingId }
      });

      if (error) throw error;

      toast({
        title: t('appointments.actions.confirmSuccess'),
        description: t('appointments.confirmSuccess'),
      });

      onSuccess?.();
    } catch (error: any) {
      console.error('Error confirming booking:', error);
      toast({
        title: t('error'),
        description: error.message || 'Failed to confirm booking',
        variant: 'destructive',
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      toast({
        title: t('error'),
        description: 'Please provide a reason for declining',
        variant: 'destructive',
      });
      return;
    }

    setIsDeclining(true);
    try {
      const { error } = await supabase.functions.invoke('decline-booking', {
        body: { 
          bookingId,
          reason: declineReason 
        }
      });

      if (error) throw error;

      toast({
        title: t('appointments.actions.declineSuccess'),
        description: t('appointments.declineSuccess'),
      });

      setDeclineReason('');
      onSuccess?.();
    } catch (error: any) {
      console.error('Error declining booking:', error);
      toast({
        title: t('error'),
        description: error.message || 'Failed to decline booking',
        variant: 'destructive',
      });
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Button
        size="sm"
        onClick={handleConfirm}
        disabled={isConfirming}
        className="bg-green-600 hover:bg-green-700"
      >
        <Check className="h-4 w-4 mr-1" />
        {isConfirming ? t('loading') : 'Confirm'}
      </Button>

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={isDeclining}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            <X className="h-4 w-4 mr-1" />
            Decline
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline Booking</AlertDialogTitle>
            <AlertDialogDescription>
              {t('appointments.declineDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="my-4">
            <label className="text-sm font-medium mb-2 block">
              {t('appointments.declineReason')}
            </label>
            <Textarea
              placeholder={t('appointments.declineReasonPlaceholder')}
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="min-h-[80px]"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDecline}
              disabled={isDeclining || !declineReason.trim()}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeclining ? t('loading') : 'Decline'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
